import React, { useMemo } from 'react';

import classNames from 'classnames';

import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';
import { RadioGroup, RadioGroupItem } from '@buildoutinc/blueprint-react/ui/RadioGroup';

import OfficesField, { UNKNOWN_OFFICE, officeOptionsOf } from './offices_field';
import OtherUsersTable from './other_users_table';
import { toSentence } from './permission_state';

import type { OupMode } from 'bundles/company/permissions/oup_user_selector';
import type { OupData } from 'components/users/types';

const MODES: { value: OupMode; label: string; description: string }[] = [
  {
    value: 'all',
    label: 'All Company Users',
    description: 'This user will be able to manage data for all the users at the company.',
  },
  {
    value: 'individual',
    label: 'Individual Users',
    description: 'This user will be able to manage data for the users selected below.',
  },
  {
    value: 'offices',
    label: 'Users in Specific Offices',
    description: 'This user will be able to manage data for users in the specific offices selected below.',
  },
];

interface Props {
  oup: OupData;
  editing: boolean;
  mode: OupMode;
  selectedUserIds: number[];
  selectedOfficeIds: number[];
  onChangeMode: (mode: OupMode) => void;
  onChangeUserIds: (ids: number[]) => void;
  onChangeOfficeIds: (ids: number[]) => void;
}

// The order the server reads them in: an office list wins over a user list.
export function oupModeOf(selectedUserIds: number[], selectedOfficeIds: number[]): OupMode {
  if (selectedOfficeIds.length > 0) return 'offices';
  if (selectedUserIds.length > 0) return 'individual';
  return 'all';
}

function readSummary(
  oup: OupData, mode: OupMode, selectedUserIds: number[], selectedOfficeIds: number[],
): React.ReactNode {
  if (mode === 'offices') {
    const names = selectedOfficeIds.map(id => oup.officesById[id]?.name ?? UNKNOWN_OFFICE);

    return <p className="mb-0 fw-semibold" data-testid="other-users-summary">{`Users in ${toSentence(names)}`}</p>;
  }

  if (mode === 'individual') {
    const names = oup.users
      .filter(user => selectedUserIds.includes(user.id))
      .map(user => `${user.firstName} ${user.lastName}`);

    // Every stored user has been deactivated. The server still limits this person to them, so it is not "all".
    if (names.length === 0) {
      return <p className="mb-0 fw-semibold" data-testid="other-users-summary">No active users selected</p>;
    }

    // A long list names the first few; the count above already says how many there are.
    const shownNames = names.length > 4 ? [...names.slice(0, 3), `${names.length - 3} others`] : names;

    return (
      <>
        <p className="mb-0 fw-semibold" data-testid="other-users-summary">
          {`${names.length} individual user${names.length === 1 ? '' : 's'}`}
        </p>
        <p className="mb-0 text-muted fs-small" data-testid="other-users-names">{toSentence(shownNames)}</p>
      </>
    );
  }

  return <p className="mb-0 fw-semibold" data-testid="other-users-summary">All company users</p>;
}

export default function OtherUsersCard({
  oup, editing, mode, selectedUserIds, selectedOfficeIds, onChangeMode, onChangeUserIds, onChangeOfficeIds,
}: Props) {
  const officeOptions = useMemo(() => officeOptionsOf(oup.officesById), [oup.officesById]);

  return (
    <Card className="mb-4">
      <Card.Header>
        <Card.Title className="fw-semibold fs-large">Other Users</Card.Title>
        <p className="mb-0 text-muted fs-small">Who &quot;other users&quot; means in the permissions above.</p>
      </Card.Header>
      <Card.Body>
        {editing ? (
          <>
            <RadioGroup
              aria-label="Other users"
              className="d-flex flex-column gap-2"
              value={mode}
              onValueChange={value => onChangeMode(value as OupMode)}
            >
              {MODES.map(option => (
                <Label
                  className={classNames('d-flex gap-2 border rounded p-3 mb-0 w-100', {
                    'border-primary': mode === option.value,
                  })}
                  htmlFor={`other-users-${option.value}`}
                  key={option.value}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onChangeMode(option.value)}
                >
                  <RadioGroupItem
                    className="mt-1 flex-shrink-0"
                    id={`other-users-${option.value}`}
                    value={option.value}
                  />
                  <span>
                    <span className="fw-semibold d-block">{option.label}</span>
                    <span className="text-muted fs-small">{option.description}</span>
                  </span>
                </Label>
              ))}
            </RadioGroup>

            {mode === 'individual' && (
              <div className="mt-4">
                <OtherUsersTable
                  officeOptions={officeOptions}
                  officesById={oup.officesById}
                  selectedUserIds={selectedUserIds}
                  users={oup.users}
                  onChange={onChangeUserIds}
                />
              </div>
            )}

            {mode === 'offices' && (
              <div className="mt-4">
                <Label htmlFor="other-users-office-ids">Offices this user can manage</Label>
                <OfficesField
                  inputId="other-users-office-ids"
                  options={officeOptions}
                  selectedIds={selectedOfficeIds}
                  onChange={onChangeOfficeIds}
                />
              </div>
            )}
          </>
        ) : readSummary(oup, mode, selectedUserIds, selectedOfficeIds)}
      </Card.Body>
    </Card>
  );
}
